import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle2, Home, FileText, Users, Scale } from 'lucide-react';

const EstatePlanningPage = () => {
  const strategies = [
    { icon: FileText, title: "Life Insurance Trusts", description: "Irrevocable trusts that own life insurance policies for estate tax benefits" },
    { icon: Scale, title: "Estate Tax Liquidity", description: "Life insurance to pay estate taxes without selling assets" },
    { icon: Users, title: "Legacy Planning", description: "Structured wealth transfer to future generations" },
    { icon: Home, title: "Wealth Transfer Structures", description: "Efficient strategies to pass assets to heirs" }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="estate-planning-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Link to="/services" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
              ← Back to Services
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Estate Planning Guidance
            </h1>
            <p className="text-xl text-slate-300 mb-6">
              Protect and Transfer Wealth Efficiently
            </p>
            <p className="text-slate-400 leading-relaxed">
              Estate planning ensures assets are transferred efficiently while minimizing tax exposure and protecting future generations. Life insurance plays a crucial role in comprehensive estate plans.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl text-white mb-12 text-center">Estate Planning Strategies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {strategies.map((strategy, idx) => {
              const Icon = strategy.icon;
              return (
                <div key={idx} className="bg-slate-900/50 border border-slate-800 p-8 hover:border-[#C5A059]/30 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#C5A059]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg mb-2">{strategy.title}</h3>
                      <p className="text-slate-400">{strategy.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">Our Approach</h2>
              <p className="text-slate-400 mb-6">
                ShopCompareInsure works alongside estate attorneys and tax advisors to create coordinated planning strategies. We focus on the life insurance component while ensuring it integrates seamlessly with your overall estate plan.
              </p>
              <ul className="space-y-3">
                {[
                  "Coordination with existing legal and tax advisors",
                  "Analysis of current estate structure",
                  "Life insurance policy design and placement",
                  "Ongoing review and adjustment as laws change"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059]" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 p-8">
              <Shield className="w-12 h-12 text-[#C5A059] mb-6" />
              <h3 className="text-white font-semibold text-xl mb-4">Important Notice</h3>
              <p className="text-slate-400 text-sm">
                ShopCompareInsure provides life insurance solutions as part of estate planning strategies. We are not attorneys or CPAs and do not provide legal or tax advice. Always consult with qualified professionals for comprehensive estate planning guidance.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Start Your Estate Planning Conversation</h2>
          <p className="text-slate-400 mb-8">Speak with an advisor about how life insurance fits into your estate plan.</p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Request Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EstatePlanningPage;
